import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ScatterChart, Scatter, Cell, Legend
} from 'recharts';
import api from '../api';
import { Animal } from '../types';

const BREED_LABELS: Record<string, string> = {
  qorabosh: 'Qorabosh', golshtin: 'Golshtin', simmental: 'Simmental',
  abuerdeen: 'Aberdeen', mahalliy: 'Mahalliy',
};

export default function Analytics() {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAnimals().then(r => { setAnimals(r.data); setLoading(false); });
  }, []);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const active = animals.filter(a => a.status !== 'sotildi');

  // FCR by breed
  const byBreed: Record<string, { gains: number[]; recs: number[] }> = {};
  active.forEach(a => {
    if (!byBreed[a.breed]) byBreed[a.breed] = { gains: [], recs: [] };
    if (a.daily_gain > 0) byBreed[a.breed].gains.push(a.daily_gain);
    if (a.latest_recommendation) byBreed[a.breed].recs.push(a.latest_recommendation.fcr);
  });
  const breedStats = Object.entries(byBreed).map(([breed, d]) => ({
    breed: BREED_LABELS[breed] || breed,
    avg_gain: d.gains.length ? +(d.gains.reduce((a, b) => a + b, 0) / d.gains.length).toFixed(3) : 0,
    avg_fcr: d.recs.length ? +(d.recs.reduce((a, b) => a + b, 0) / d.recs.length).toFixed(2) : 0,
    count: d.gains.length,
  })).filter(b => b.count > 0);

  // Weight distribution buckets
  const buckets: Record<string, number> = { '200-250': 0, '250-300': 0, '300-350': 0, '350-400': 0, '400+': 0 };
  active.forEach(a => {
    const w = a.latest_weight;
    if (w < 250) buckets['200-250']++;
    else if (w < 300) buckets['250-300']++;
    else if (w < 350) buckets['300-350']++;
    else if (w < 400) buckets['350-400']++;
    else buckets['400+']++;
  });
  const distData = Object.entries(buckets).map(([range, count]) => ({ range, count }));

  // Scatter: weight vs daily gain
  const scatterData = active.filter(a => a.daily_gain > 0).map(a => ({
    weight: a.latest_weight,
    gain: a.daily_gain,
    name: a.tag_id,
    breed: BREED_LABELS[a.breed] || a.breed,
  }));

  // Sell readiness
  const sellData = active.map(a => {
    const rec = a.latest_recommendation;
    return {
      tag: a.tag_id,
      name: a.tag_id,
      current: a.latest_weight,
      target: rec?.target_weight || 0,
      pct: rec?.target_weight ? Math.min(100, Math.round((a.latest_weight / rec.target_weight) * 100)) : 0,
      sell_ready: rec?.sell_ready || false,
    };
  }).filter(a => a.target > 0).sort((a, b) => b.pct - a.pct).slice(0, 10);

  // Summary metrics
  const totalAnimals = active.length;
  const avgGain = active.filter(a => a.daily_gain > 0).reduce((s, a) => s + a.daily_gain, 0) / (active.filter(a => a.daily_gain > 0).length || 1);
  const avgFCR = active.filter(a => a.latest_recommendation).reduce((s, a) => s + (a.latest_recommendation?.fcr || 0), 0) / (active.filter(a => a.latest_recommendation).length || 1);
  const sellReady = active.filter(a => a.latest_recommendation?.sell_ready).length;

  return (
    <div className="page-body">
      {/* Summary stats */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card teal">
          <div className="stat-label">Faol chorvalar</div>
          <div className="stat-value">{totalAnimals}</div>
          <div className="stat-sub">Tahlil uchun</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">O'rtacha ADG</div>
          <div className="stat-value">{avgGain.toFixed(3)} <span style={{ fontSize: 14 }}>kg</span></div>
          <div className="stat-sub">Kunlik o'rtacha o'sish</div>
        </div>
        <div className="stat-card amber">
          <div className="stat-label">O'rtacha FCR</div>
          <div className="stat-value">{avgFCR.toFixed(2)}</div>
          <div className="stat-sub">Ozuqa konversiyasi</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid var(--teal)' }}>
          <div className="stat-label">Sotishga tayyor</div>
          <div className="stat-value" style={{ color: 'var(--teal)' }}>{sellReady}</div>
          <div className="stat-sub">ta chorva</div>
        </div>
      </div>

      {/* Row 1 */}
      <div className="charts-grid">
        <div className="card">
          <div className="card-header"><h3>Zot bo'yicha kunlik o'sish (ADG)</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={breedStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="breed" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: any) => [`${v} kg/kun`, 'ADG']} />
                <Bar dataKey="avg_gain" fill="#0D7E6E" radius={[4, 4, 0, 0]}>
                  {breedStats.map((_, i) => (
                    <Cell key={i} fill={['#0D7E6E', '#14A88A', '#E5831A', '#5DCAA5', '#0F6E56'][i % 5]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Zot bo'yicha FCR</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={breedStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="breed" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} domain={[4, 9]} />
                <Tooltip formatter={(v: any) => [v, 'FCR (past = yaxshiroq)']} />
                <Bar dataKey="avg_fcr" radius={[4, 4, 0, 0]}>
                  {breedStats.map((entry, i) => (
                    <Cell key={i} fill={entry.avg_fcr < 6.5 ? '#0D7E6E' : entry.avg_fcr < 7.5 ? '#E5831A' : '#C0392B'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: 'var(--gray)', textAlign: 'center', marginTop: 8 }}>
              🟢 &lt;6.5 Yaxshi · 🟠 6.5–7.5 O'rta · 🔴 &gt;7.5 Yomon
            </div>
          </div>
        </div>
      </div>

      {/* Row 2 */}
      <div className="charts-grid">
        <div className="card">
          <div className="card-header"><h3>Vazn taqsimoti</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={distData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip formatter={(v: any) => [v, 'Chorva soni']} />
                <Bar dataKey="count" fill="#14A88A" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Vazn ↔ Kunlik o'sish</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="weight" type="number" name="Vazn" tick={{ fontSize: 11 }} unit=" kg" domain={['auto', 'auto']} />
                <YAxis dataKey="gain" type="number" name="ADG" tick={{ fontSize: 11 }} unit=" kg" domain={['auto', 'auto']} />
                <Tooltip cursor={{ strokeDasharray: '3 3' }}
                  content={({ payload }) => payload?.length ? (
                    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                      <div><strong>{payload[0].payload.name}</strong> ({payload[0].payload.breed})</div>
                      <div>Vazn: {payload[0].payload.weight} kg</div>
                      <div>ADG: {payload[0].payload.gain} kg/kun</div>
                    </div>
                  ) : null} />
                <Scatter data={scatterData} fill="#0D7E6E" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Sell readiness progress */}
      <div className="card">
        <div className="card-header"><h3>Maqsad vazniga yaqinlik (%)</h3></div>
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sellData.map(a => (
              <div key={a.tag} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="tag-id" style={{ minWidth: 72 }}>{a.tag}</span>
                                <div style={{ flex: 1, background: 'var(--gray-light)', borderRadius: 8, height: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{
                    width: `${a.pct}%`, height: '100%', borderRadius: 8,
                    background: a.sell_ready ? 'var(--teal)' : a.pct > 75 ? '#14A88A' : 'var(--amber)',
                    transition: 'width 0.3s',
                  }} />
                </div>
                <span style={{ minWidth: 44, fontSize: 13, fontWeight: 600, color: a.sell_ready ? 'var(--teal)' : 'var(--text-mid)' }}>
                  {a.pct}%
                </span>
                {a.sell_ready && <span className="badge badge-sell">Tayyor</span>}
                <span style={{ fontSize: 12, color: 'var(--gray)', minWidth: 100 }}>{a.current} / {a.target} kg</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
